import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/** @jsxRuntime automatic */
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { selectStarCommandNotices, starCommandConnected, starCommandCurrent, verifiedStarCommandReceipt } from './StarCommandPresentation.js';
export const STAR_COMMAND_OPERATION_LIMIT = 512;
export const STAR_COMMAND_NAVIGATION_LIMIT = 32;
const ADAPTER_DEADLINE_MS = 15_000;
const sessions = new WeakMap();
const snapshotTokens = new WeakMap();
function snapshotToken(snapshot) {
    if (!snapshot)
        return undefined;
    let token = snapshotTokens.get(snapshot);
    if (!token) {
        token = {};
        snapshotTokens.set(snapshot, token);
    }
    return token;
}
const binding = (notice) => JSON.stringify([notice.appId, notice.id]);
const EMPTY_SELECTION = new Set();
/** The center's one selection across its sections: a Star Command notice's key in it. */
export const starCommandSelectionKey = (notice) => `s:${binding(notice)}`;
const notify = (session) => { session.version++; for (const listener of session.listeners)
    listener(); };
/** Release the UI wait even if an adapter ignores cancellation; its late response never changes receipt custody. */
function adapterWithinDeadline(controller, work) {
    return new Promise((resolve, reject) => {
        let settled = false;
        const finish = (done) => {
            if (settled)
                return;
            settled = true;
            clearTimeout(timer);
            controller.signal.removeEventListener('abort', abort);
            done();
        };
        const abort = () => finish(() => reject(new Error('Adapter operation interrupted')));
        const timer = setTimeout(() => controller.abort(), ADAPTER_DEADLINE_MS);
        controller.signal.addEventListener('abort', abort, { once: true });
        if (controller.signal.aborted) {
            abort();
            return;
        }
        void Promise.resolve().then(() => controller.signal.aborted ? Promise.reject(new Error('Adapter operation interrupted')) : work())
            .then(value => finish(() => resolve(value)), error => finish(() => reject(error)));
    });
}
function suspend(session) {
    let changed = false;
    for (const [key, operation] of session.operations) {
        if (operation.state !== 'saving')
            continue;
        operation.controller?.abort();
        session.operations.set(key, { request: operation.request, state: 'uncertain',
            message: 'Dismissal is not confirmed. Check its original receipt before trying again.' });
        changed = true;
    }
    for (const navigation of session.navigation.values())
        navigation.controller?.abort();
    if (session.navigation.size) {
        session.navigation.clear();
        changed = true;
    }
    if (session.navigationCapacityMessage) {
        session.navigationCapacityMessage = '';
        changed = true;
    }
    if (changed)
        notify(session);
}
function sessionFor(adapter, accountKey) {
    let owners = sessions.get(adapter);
    if (!owners) {
        owners = new Map();
        sessions.set(adapter, owners);
    }
    let session = owners.get(accountKey);
    if (!session) {
        session = { operations: new Map(), listeners: new Set(), version: 0, capacityMessage: '', navigationCapacityMessage: '', navigation: new Map() };
        owners.set(accountKey, session);
    }
    return session;
}
/** Called by the owning center on account/adapter replacement or feature removal; panel close alone retains receipts. */
export function disposeStarCommandPresentation(adapter, accountKey) {
    if (!adapter)
        return;
    const owners = sessions.get(adapter), session = owners?.get(accountKey);
    if (!session)
        return;
    for (const operation of session.operations.values())
        operation.controller?.abort();
    for (const operation of session.navigation.values())
        operation.controller?.abort();
    session.operations.clear();
    session.navigation.clear();
    session.capacityMessage = '';
    session.navigationCapacityMessage = '';
    notify(session);
    owners?.delete(accountKey);
}
/** Absent on web and Office; only a bound desktop presentation can expose the section. */
export function StarCommandSection({ presentation, accountKey, filter, selection, onBulkState }) {
    if (!starCommandConnected(presentation, accountKey) || filter.category === 'question'
        || filter.source !== 'all' && filter.source !== 'star-command' && !filter.source.startsWith('star-command:'))
        return null;
    return _jsx(ConnectedStarCommand, { presentation: presentation, accountKey: accountKey, filter: filter, selection: selection, onBulkState: onBulkState });
}
function ConnectedStarCommand({ presentation, accountKey, filter, selection, onBulkState }) {
    const adapter = presentation.adapter, session = sessionFor(adapter, accountKey);
    useSyncExternalStore(listener => { session.listeners.add(listener); return () => session.listeners.delete(listener); }, () => session.version, () => session.version);
    const current = useRef({ presentation, accountKey, session });
    current.current = { presentation, accountKey, session };
    const mounted = useRef(false);
    // "Dismiss selected" (owner, 2026-10-27): the center owns the one selection across its sections; this section runs the
    // Star Command half of a pass and tells the center which keys it may dismiss and whether a pass is running.
    const selected = selection?.selected ?? EMPTY_SELECTION;
    const [bulk, setBulk] = useState(null);
    const bulkRunning = useRef(false);
    const bulkBusy = Boolean(bulk && bulk.accountKey === accountKey && bulk.busy);
    const dismissRef = useRef(() => { });
    const stableDismiss = useRef((keys) => dismissRef.current(keys)).current;
    const reportedKeys = starCommandCurrent(presentation, accountKey)
        ? selectStarCommandNotices(presentation, accountKey, filter).filter(candidate => !session.operations.get(binding(candidate))).map(starCommandSelectionKey).join('\n') : '';
    useEffect(() => { onBulkState?.({ keys: reportedKeys ? reportedKeys.split('\n') : [], busy: bulkBusy, dismiss: stableDismiss }); }, [onBulkState, reportedKeys, bulkBusy, stableDismiss]);
    useEffect(() => () => onBulkState?.({ keys: [], busy: false, dismiss: () => { } }), [onBulkState]);
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            // Abort transport work, retain original dismissal custody, and never navigate after the surface disappears.
            suspend(session);
        };
    }, [session]);
    useEffect(() => {
        if (starCommandCurrent(presentation, accountKey))
            return;
        suspend(session);
    }, [presentation, accountKey, session]);
    useEffect(() => {
        if (!starCommandCurrent(presentation, accountKey))
            return;
        // Only a newly verified source snapshot may make a definitively refused dismissal eligible again.
        let changed = false;
        const notices = new Map(presentation.snapshot.items.map(notice => [binding(notice), notice]));
        for (const [key, operation] of session.operations) {
            const notice = notices.get(key);
            if (operation && operation.settledSnapshot !== snapshotToken(presentation.snapshot)
                && (operation.state === 'failed' || operation.state === 'applied' && (!notice || operation.request.noticeRevision !== notice.revision))) {
                session.operations.delete(key);
                changed = true;
            }
        }
        for (const [key, navigation] of session.navigation)
            if (notices.get(key)?.revision !== navigation.revision) {
                navigation.controller?.abort();
                session.navigation.delete(key);
                changed = true;
            }
        if (session.operations.size < STAR_COMMAND_OPERATION_LIMIT && session.capacityMessage) {
            session.capacityMessage = '';
            changed = true;
        }
        if (session.navigation.size < STAR_COMMAND_NAVIGATION_LIMIT && session.navigationCapacityMessage) {
            session.navigationCapacityMessage = '';
            changed = true;
        }
        if (changed)
            notify(session);
    }, [presentation, accountKey, session]);
    const usable = () => mounted.current && current.current.session === session && current.current.accountKey === accountKey
        && starCommandCurrent(current.current.presentation, accountKey);
    const changed = () => {
        try {
            if (usable())
                void Promise.resolve(current.current.presentation.onChanged?.()).catch(() => { });
        }
        catch { /* A refresh callback cannot revoke an acknowledged receipt. */ }
    };
    async function dismiss(notice, lookup, retry = false, quiet = false) {
        if (!usable() || typeof navigator !== 'undefined' && !navigator.onLine)
            return;
        const key = binding(notice), existing = session.operations.get(key);
        if (existing?.state === 'saving' || existing?.state === 'applied' || !lookup && !retry && existing)
            return;
        if (retry && (existing?.state !== 'retryable'
            || !current.current.presentation.snapshot?.items.some(item => binding(item) === key && item.revision === notice.revision)))
            return;
        if (!lookup && !retry && session.operations.size >= STAR_COMMAND_OPERATION_LIMIT) {
            session.capacityMessage = 'Too many dismissals await confirmation. Check existing receipts before dismissing more.';
            notify(session);
            return;
        }
        const request = lookup || retry ? existing?.request : { accountKey, clientRequestId: crypto.randomUUID(), noticeId: notice.id, noticeRevision: notice.revision, appId: notice.appId };
        if (!request)
            return;
        const controller = new AbortController();
        session.operations.set(key, { request, state: 'saving', message: lookup ? 'Checking the original dismissal receipt…' : 'Saving dismissal…', controller });
        notify(session);
        try {
            const receipt = await adapterWithinDeadline(controller, () => lookup ? adapter.receipt(request, controller.signal) : adapter.dismiss(request, controller.signal));
            if (controller.signal.aborted) {
                if (session.operations.get(key)?.controller === controller) {
                    session.operations.set(key, { request, state: 'uncertain', message: 'Dismissal is not confirmed. Check its original receipt before trying again.' });
                    notify(session);
                }
                return;
            }
            if (lookup && receipt === null) {
                session.operations.set(key, { request, state: 'retryable', message: 'The source has no receipt for the original dismissal. Retry sends the same request for its original version; the source will refuse it if that version has changed.' });
                notify(session);
                return;
            }
            if (!verifiedStarCommandReceipt(receipt, request))
                throw new Error('Unverified dismissal receipt');
            session.operations.set(key, { request, state: receipt.state, settledSnapshot: snapshotToken(current.current.presentation.snapshot), message: receipt.state === 'applied'
                    ? 'Dismissal confirmed. Waiting for the source list to refresh.' : receipt.state === 'pending'
                    ? 'Dismissal is pending. Check its original receipt.' : 'The source did not dismiss this item. Refresh before trying again.' });
            notify(session);
            if (receipt.state !== 'pending' && !quiet)
                changed();
        }
        catch (error) {
            if (controller.signal.aborted) {
                if (session.operations.get(key)?.controller === controller) {
                    session.operations.set(key, { request, state: 'uncertain', message: 'Dismissal is not confirmed. Check its original receipt before trying again.' });
                    notify(session);
                }
                return;
            }
            const status = Number(error && typeof error === 'object' && 'status' in error ? error.status : undefined);
            const definite = !lookup && [400, 401, 403, 404, 409, 410].includes(status);
            session.operations.set(key, { request, state: definite ? 'failed' : 'uncertain', settledSnapshot: definite ? snapshotToken(current.current.presentation.snapshot) : undefined, message: definite
                    ? 'The source refused this dismissal. Refresh before trying again.' : 'Dismissal is not confirmed. Check its original receipt before trying again.' });
            notify(session);
            if (definite && !quiet)
                changed();
        }
    }
    /** Dismiss every selected notice that has no dismissal yet, one after another through the receipt-checked path, then refresh the source list once. */
    async function dismissSelected(targets) {
        if (bulkRunning.current || !targets.length)
            return;
        bulkRunning.current = true;
        setBulk({ accountKey, busy: true, done: 0, total: targets.length });
        let done = 0;
        try {
            for (const target of targets) {
                if (!usable() || session.capacityMessage)
                    break;
                await dismiss(target, false, false, true);
                if (session.operations.get(binding(target))?.state === 'applied') {
                    done++;
                    setBulk({ accountKey, busy: true, done, total: targets.length });
                }
            }
        }
        finally {
            bulkRunning.current = false;
            setBulk({ accountKey, busy: false, done, total: targets.length });
            if (done)
                changed();
        }
    }
    dismissRef.current = keys => {
        const targets = selectStarCommandNotices(current.current.presentation, accountKey, filter)
            .filter(candidate => keys.has(starCommandSelectionKey(candidate)) && !session.operations.get(binding(candidate)));
        void dismissSelected(targets);
    };
    async function navigate(notice) {
        if (!usable() || session.navigation.get(binding(notice))?.busy)
            return;
        const key = binding(notice), controller = new AbortController();
        for (const [previous, navigation] of session.navigation)
            if (!navigation.busy && session.navigation.size >= STAR_COMMAND_NAVIGATION_LIMIT)
                session.navigation.delete(previous);
        if (!session.navigation.has(key) && session.navigation.size >= STAR_COMMAND_NAVIGATION_LIMIT) {
            session.navigationCapacityMessage = 'Several apps are still opening. Wait for those requests to finish before opening more.';
            notify(session);
            return;
        }
        session.navigationCapacityMessage = '';
        session.navigation.set(key, { busy: true, message: '', revision: notice.revision, controller });
        notify(session);
        const currentNotice = () => usable() && session.navigation.get(key)?.controller === controller && !controller.signal.aborted
            && current.current.presentation.snapshot?.items.some(value => binding(value) === key && value.revision === notice.revision);
        try {
            const result = await adapterWithinDeadline(controller, async () => {
                let result = adapter.openNotice ? await adapter.openNotice(notice, controller.signal) : 'unavailable';
                if (!currentNotice())
                    return 'cancelled';
                if (result === 'unavailable' && adapter.openApp)
                    result = await adapter.openApp(notice.appId, controller.signal);
                return result;
            });
            if (!currentNotice())
                return;
            if (result === 'opened')
                session.navigation.delete(key);
            else
                session.navigation.set(key, { busy: false, revision: notice.revision, message: 'The original app is unavailable. No alternative destination was opened.' });
            notify(session);
        }
        catch {
            if (!usable() || session.navigation.get(key)?.controller !== controller)
                return;
            session.navigation.set(key, { busy: false, revision: notice.revision, message: controller.signal.aborted
                    ? 'Opening the app timed out. Its result is unconfirmed; check the app before trying again.' : 'Opening the original app was not confirmed. Try again when it is available.' });
            notify(session);
        }
    }
    function celebrate(notice) {
        if (!usable() || !adapter.celebrate)
            return;
        const source = current.current.presentation;
        const latest = source.snapshot?.items.find(item => binding(item) === binding(notice) && item.revision === notice.revision);
        const state = source.celebrations?.[notice.id];
        if (latest?.effect !== 'celebrate' || !state || state.state === 'played' || state.state === 'playing' || state.reason === 'reduced-motion')
            return;
        adapter.celebrate(latest);
    }
    if (!starCommandCurrent(presentation, accountKey))
        return _jsxs("section", { className: "gyro-attention-star-command", "aria-label": "Star Command", children: [_jsx("h3", { children: "Star Command" }), _jsx("p", { role: "status", children: "Refresh to verify access to app notifications." })] });
    const notices = selectStarCommandNotices(presentation, accountKey, filter);
    if (!notices.length)
        return null;
    return _jsxs("section", { className: "gyro-attention-star-command", "aria-label": "Star Command", children: [_jsxs("h3", { className: "gyro-attention-group-title", children: ["Star Command ", _jsx("span", { children: notices.length })] }), bulkBusy && bulk && _jsxs("p", { role: "status", children: ["Dismissing ", bulk.done, " of ", bulk.total, "\u2026"] }), bulk && bulk.accountKey === accountKey && !bulk.busy && _jsxs("p", { role: "status", children: ["Dismissed ", bulk.done, " of ", bulk.total, " selected ", bulk.total === 1 ? 'notification' : 'notifications', "."] }), session.capacityMessage && _jsx("p", { role: "alert", children: session.capacityMessage }), session.navigationCapacityMessage && _jsx("p", { role: "alert", children: session.navigationCapacityMessage }), notices.map(notice => {
                const key = binding(notice), operation = session.operations.get(key), navigation = session.navigation.get(key);
                const previousVersionSaved = operation?.state === 'applied' && operation.request.noticeRevision !== notice.revision;
                const celebration = notice.effect === 'celebrate' ? presentation.celebrations?.[notice.id] : undefined;
                return _jsxs("article", { className: "gyro-attention-item", "data-star-command-id": notice.id, "data-unread": !notice.read, children: [_jsxs("header", { className: "gyro-attention-item-heading", children: [selection && _jsx("input", { type: "checkbox", className: "gyro-attention-select", "aria-label": `Select ${notice.title}`, checked: selected.has(`s:${key}`), disabled: Boolean(operation) || bulkBusy, onChange: event => selection.toggle(`s:${key}`, event.target.checked) }), _jsx("div", { className: "gyro-attention-avatar", "aria-hidden": "true", children: notice.icon }), _jsxs("div", { className: "gyro-attention-identity", children: [_jsx("strong", { children: notice.appName }), _jsx("small", { children: "App notification" })] })] }), _jsx("h3", { children: notice.title }), _jsx("p", { className: "gyro-attention-body", children: notice.description }), _jsxs("footer", { children: [_jsx("time", { dateTime: notice.occurredAt, children: new Date(notice.occurredAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) }), celebration && adapter.celebrate && _jsx("button", { type: "button", className: "gyro-btn ghost", disabled: celebration.state === 'playing' || celebration.state === 'played' || celebration.reason === 'reduced-motion', onClick: () => celebrate(notice), children: celebration.reason === 'reduced-motion' ? 'This PC has animations off'
                                        : celebration.state === 'waiting' ? 'Waiting for a free moment' : celebration.state === 'ready' ? 'Celebrate'
                                            : celebration.state === 'playing' ? 'Playing' : 'Celebrated ✓' }), (adapter.openNotice || adapter.openApp) && _jsx("button", { type: "button", className: "gyro-btn ghost", disabled: navigation?.busy, onClick: () => void navigate(notice), children: navigation?.busy ? 'Opening…' : adapter.openNotice ? 'Open notification' : `Open ${notice.appName}` }), _jsx("button", { type: "button", className: "gyro-btn ghost", disabled: Boolean(operation), onClick: () => void dismiss(notice, false), children: operation?.state === 'saving' ? 'Saving dismissal…' : previousVersionSaved ? 'Earlier version dismissed' : operation?.state === 'applied' ? 'Dismissed' : 'Dismiss' }), (operation?.state === 'uncertain' || operation?.state === 'pending' || operation?.state === 'retryable') && _jsx("button", { type: "button", className: "gyro-btn ghost", onClick: () => void dismiss(notice, true), children: "Check dismissal receipt" }), operation?.state === 'retryable' && _jsx("button", { type: "button", className: "gyro-btn ghost", onClick: () => void dismiss(notice, false, true), children: "Retry original dismissal" })] }), operation?.message && _jsx("p", { role: operation.state === 'failed' || operation.state === 'uncertain' ? 'alert' : 'status', children: previousVersionSaved ? 'Dismissal of an earlier version is confirmed. Refresh to inspect this version.' : operation.message }), navigation?.message && _jsx("p", { role: "status", children: navigation.message })] }, key);
            })] });
}
//# sourceMappingURL=StarCommandSection.js.map